import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { isFlagEnabled } from "js/utilities";
import { Disclosure } from "@headlessui/react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { convertToTitleCase, stringFormattingToCurrency } from "js/formatting";
import { usePolicyMapper } from "hooks/usePolicyMapper";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Toast from "components/General/Toast";
import ConfirmModal from "components/ModalDialogs/ConfirmModal";
import getPaymentUID from "service/paymentServices/getPaymentUID";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import PaymentPayor from "components/AgentPortal/MakeAPayment/PaymentPayor";
import PaymentAmount from "components/AgentPortal/MakeAPayment/PaymentAmount";
import PaymentMethod from "components/AgentPortal/MakeAPayment/PaymentMethod";
import PaymentPolicy from "components/AgentPortal/MakeAPayment/PaymentPolicy";
import AgentAuthorizePaymentModal from "components/AgentPortal/ModalDialogs/AgentAuthorizePaymentModal";
import AuthorizePaymentNameValidationModal from "components/ModalDialogs/AuthorizePaymentNameValidationModal";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

export default function MakeAPayment() {
  let navigate = useNavigate();
  const { policy_id } = useParams();
  const { csrUser } = useSelector((s) => s.csrUser);
  const { user } = useSelector((s) => s.user);
  const { agentPolicies: policies } = useContext(AgentData);
  const { t } = useTranslation();
  const {
    register,
    setValue,
    trigger,
    getFieldState,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      policyNumber: policy_id,
    },
  });
  const [errorTitle, setErrorTitle] = useState();
  const [errorDisc, setErrorDisc] = useState();
  const [userValErrorModalState, setUserValErrorModalState] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [authorizeModal, setAuthorizeModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [policy, setPolicy] = useState();
  const [payment, setPayment] = useState();
  const [selectedPolicyId, setSelectedPolicyId] = useState();
  const [oldPolicyId, setOldPolicyId] = useState();
  const [uid, setUid] = useState();
  const { policyDetail } = usePolicyMapper(selectedPolicyId);

  const [
    creditCardValidationCheckModalState,
    setCreditCardValidationCheckModalState,
  ] = useState(false);
  const [toastDetail, setToastDetail] = useState({
    type: undefined,
    title: "title",
    message: "message",
  });

  async function advanceModal() {
    if (!csrUser?.userId) {
      if (isFlagEnabled("ENABLE_NAME_VALIDATION_ON_AUTHORIZE_PAYMENT_MODAL"))
        return setCreditCardValidationCheckModalState(true);

      let amount = (
        getValues("amount").includes(".")
          ? getValues("amount")
          : `${getValues("amount")}.00`
      )
        .replace("$", "")
        .replace(",", "");
      let selectedPolicy = policies.find(
        (x) => x.oldPolicy === getValues("policyNumber")
      );
      if (parseFloat(amount) !== 0 && selectedPolicy) {
        let response = await getPaymentUID(
          stringFormattingToCurrency(amount.replace("$", "").replace(",", "")),
          `${selectedPolicy?.contractID}`,
          `${getValues("firstName")} ${getValues("lastName")}`,
          "agent",
          `${user.userId}`,
          `${selectedPolicy?.blockCode}`
        ).catch((error) => {
          console.log(error);
        });
        //call hppServiceRedirect api and pass UID
        if (response?.uid) {
          setUid(response?.uid);
        }
        setAuthorizeModal(true);
        setConfirmationModal(false);
      } else {
        setErrorTitle("Error input");
        setErrorDisc("Amount must be greater than 0");
        setUserValErrorModalState(true);
      }
    } else {
      setErrorTitle("CSR validation error");
      setErrorDisc(
        "Customer service representative user can not make a payment."
      );
      setUserValErrorModalState(true);
    }
  }
  const cancel = () => {
    navigate("/dashboard", { replace: true });
  };
  useEffect(() => {
    if (policies?.length > 0) {
      let p = policies.find(
        (x) => x.contractID === policy_id || x.contractID === selectedPolicyId
      );
      if (p) {
        setPolicy(p);
        setOldPolicyId(p?.oldPolicy);
        setSelectedPolicyId(p?.contractID);
        setCurrentStep(2);
      }
    }
  }, [policy_id, selectedPolicyId]);

  const location = useLocation();
  const isPolicyLoaded =
    policyDetail &&
    Object.keys(policyDetail).length > 0 &&
    Array.isArray(policyDetail.parties);
  const isNonNaturalEntity = Boolean(
    policyDetail?.parties?.find(
      (p) =>
        p?.person && p.person.partyTypeCode === "OW" && p.person.taxType === "1"
    )
  );

  useBreadcrumb(
    location.pathname.indexOf("guest") > 0
      ? [
          { name: "Home", url: "/" },
          { name: "Make a Payment", url: "#" },
        ]
      : []
  );
  return (
    <>
      {/*
        DEVELOPMENT NOTE:

        The interacting with the input elements controls the display of the next panel
        So the input element in the components needs the aria attributes' "aria-controls" and "aria-expands"
        No sure how to get and pass the dynamic IDs assigned by the Headless UI Disclosure between the page and the components
        We might need to change the interaction to have next buttons like the registration page

      */}
      <div className="container mb-8">
        <h1>{t("AgentMakeAPayment-Title")}</h1>
        <p>{t("AgentMakeAPayment-SubTitle")}</p>
        <hr />
      </div>

      {toastDetail.type && (
        <div className="ui-component-spacing">
          <Toast {...toastDetail} />
        </div>
      )}

      <form className="container mb-8">
        <div className="ui-form-make-payment container">
          {/* <!-- Step 1 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-6 mb-4">
                <div
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 1
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  1
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>
            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <Disclosure as="div">
                  {({ open }) => (
                    <>
                      <Disclosure.Button as="div" role="presentation">
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("AgentMakeAPayment-step1-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("AgentMakeAPayment-Step1")}
                          </div>
                        </div>
                      </Disclosure.Button>

                      <div className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light">
                        {currentStep >= 1 && (
                          <Disclosure.Panel static className="pb-2 text-sm">
                            {/* <p className="mb-4 text-sm">
                              Enter or verify policy number.
                            </p> */}
                            <p className="mb-4 text-sm font-bold">
                              {t("AgentMakeAPayment-Step1-Note")}
                            </p>
                            <PaymentPolicy
                              register={register}
                              validate={trigger}
                              getFieldState={getFieldState}
                              errors={errors}
                              setCurrentStep={setCurrentStep}
                              policy_Id={oldPolicyId}
                              setValue={setValue}
                              getValues={getValues}
                              selectedPolicyId={selectedPolicyId}
                              setSelectedPolicyId={setSelectedPolicyId}
                              showPolicyLookup={!policy_id}
                            />
                          </Disclosure.Panel>
                        )}
                      </div>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          {/* <!-- Step 2 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 2
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  2
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>
            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <Disclosure as="div">
                  {({ open }) => (
                    <>
                      <Disclosure.Button as="div" role="presentation">
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("AgentMakeAPayment-Step2-Title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("AgentMakeAPayment-Step2")}
                          </div>
                        </div>
                      </Disclosure.Button>
                      <div
                        id="ID_paymentStep2Options"
                        className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                      >
                        {currentStep >= 2 && (
                          <Disclosure.Panel className="pb-2 text-sm" static>
                            {/* <p className="mb-4 text-sm">
                              Select the premium payment amount.
                            </p> */}

                            <PaymentAmount
                              panelTwoSelection={payment}
                              setPanelTwoSelection={setPayment}
                              selected={policy}
                              register={register}
                              setValue={setValue}
                              setCurrentStep={setCurrentStep}
                              currentStep={currentStep}
                              getValues={getValues}
                            />
                          </Disclosure.Panel>
                        )}
                      </div>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          {/* <!-- Step 3 --> */}
          <div
            id="ID_paymentStep3"
            className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16"
          >
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  id="ID_paymentStep3Number"
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 3
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  3
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>

            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <Disclosure as="div">
                  {({ open }) => (
                    <>
                      <Disclosure.Button as="div" role="presentation">
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("AgentMakeAPayment-Step3-Title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("AgentMakeAPayment-Step3")}
                          </div>
                        </div>
                      </Disclosure.Button>
                      <div
                        id="ID_paymentStep3Options"
                        className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                      >
                        {currentStep >= 3 && (
                          <Disclosure.Panel className="pb-2 text-sm" static>
                            {/* <p className="mb-4 text-sm">
                              Select payment method.
                            </p> */}

                            <PaymentMethod
                              register={register}
                              setValue={setValue}
                              setCurrentStep={setCurrentStep}
                              currentStep={currentStep}
                            />
                          </Disclosure.Panel>
                        )}
                      </div>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          {/* <!-- Step 4 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  id="ID_paymentStep5Number"
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 4
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  4
                </div>
              </div>
              <div className="flex h-full w-px bg-transparent mx-auto overflow-hidden"></div>
            </div>

            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <Disclosure as="div">
                  {({ open }) => (
                    <>
                      <Disclosure.Button as="div" role="presentation">
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("AgentMakeAPayment-Step4-Title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("AgentMakeAPayment-Step4")}
                          </div>
                        </div>
                      </Disclosure.Button>
                      <div
                        id="ID_paymentStep5Options"
                        className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                      >
                        {currentStep >= 4 && (
                          <Disclosure.Panel className="pb-2 text-sm" static>
                            {/* <p className="mb-4 text-sm">
                              Enter the billing details.
                            </p> */}
                            <EverlakeLoadingOverlay
                              isLoading={!isPolicyLoaded}
                              loadingText={t(
                                "AgentPolicyDetails-tab-1-loadingtext"
                              )}
                            >
                              <PaymentPayor
                                register={register}
                                validate={trigger}
                                getFieldState={getFieldState}
                                errors={errors}
                                setCurrentStep={setCurrentStep}
                                getValues={getValues}
                                isNonNaturalEntity={isNonNaturalEntity}
                              />
                            </EverlakeLoadingOverlay>
                          </Disclosure.Panel>
                        )}
                      </div>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          <div className="block text-right mb-8 md:ml-12 md:mr-6 lg:ml-28 lg:pr-16">
            <button
              type="button"
              className="ui-btn-secondary min-w-[140px]"
              aria-label="cancel make a payment"
              onClick={() => cancel()}
            >
              {t("AgentMakeAPayment-Cancel")}
            </button>
            <button
              id="ID_submitPayment"
              type="button"
              className="ui-btn-primary min-w-[140px]"
              aria-label="submit payment"
              disabled={currentStep !== 5}
              onClick={() => advanceModal()}
            >
              {t("AgentMakeAPayment-Submit")}
            </button>
          </div>
        </div>
      </form>
      <AgentAuthorizePaymentModal
        modalState={authorizeModal}
        setModalState={setAuthorizeModal}
        unauthUser={true}
        uid={uid}
      />
      <ConfirmModal
        modalState={confirmationModal}
        setModalState={setConfirmationModal}
        unauthUser={true}
      />
      <GeneralPurposeDialog
        state={userValErrorModalState}
        setState={setUserValErrorModalState}
        title={errorTitle}
        desc={errorDisc}
      />
      <AuthorizePaymentNameValidationModal
        modalState={creditCardValidationCheckModalState}
        setModalState={setCreditCardValidationCheckModalState}
        selectedPolicy={policies?.find(
          (p) => p.contractID === selectedPolicyId
        )}
        amount={getValues("amount")}
        setToastDetail={setToastDetail}
        ownerFullName={convertToTitleCase(
          `${getValues("firstName")} ${getValues("lastName")}`
        )}
        isNonNaturalEntity={isNonNaturalEntity}
      />
    </>
  );
}
